import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNav } from '../../store/nav';
import { useActiveWallet } from '../../store/wallets';
import { useToast } from '../../store/toast';
import { useSession } from '../../hooks/useSession';
import { haptic } from '../../hooks/useTelegram';
import { ApiError } from '../../lib/api';
import { fmtCoin, shortAddr } from '../../lib/format';
import { openLink } from '../../lib/links';
import {
  onrampAssetsApi,
  onrampCreateApi,
  onrampCustomerApi,
  onrampGetApi,
  onrampIdentifyApi,
  onrampQuoteApi,
  onrampResendOtpApi,
  onrampSimulatePaidApi,
  onrampStartPhoneApi,
  onrampVerifyPhoneApi,
  type OnrampCustomer,
  type OnrampQuote,
} from '../../lib/onramp';
import { CoinIcon } from '../CoinIcon';
import { FigIcon } from '../FigIcon';

const inputClass =
  'h-[60px] w-full rounded-[20px] bg-card px-4 text-[16px] text-ink outline-none placeholder:text-muted';

function buyMessage(t: (key: string, opts?: Record<string, unknown>) => string, e: unknown): string {
  if (!(e instanceof ApiError)) return t('buy.failed');
  switch (e.code) {
    case 'onramp_unavailable':
      return t('buy.unavailable');
    case 'sms_rate_limited':
      return t('buy.smsLimited');
    case 'otp_invalid':
      return t('buy.otpInvalid');
    case 'otp_expired':
      return t('buy.otpExpired');
    case 'phone_in_use':
      return t('buy.phoneInUse');
    case 'quote_expired':
      return t('buy.quoteExpired');
    case 'kyc_required':
    case 'customer_not_found':
      return t('buy.kycPendingHint');
    case 'kyc_rejected':
      return t('buy.kycRejected');
    case 'limit_exceeded':
      return t('buy.limitExceeded');
    case 'amount_below_min':
      return t('buy.amountTooSmall');
    case 'amount_above_max':
      return t('buy.amountTooLarge');
    default:
      return t('buy.failed');
  }
}

function fmtRub(n: number): string {
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isFinalOrder(status: string): boolean {
  return status === 'completed' || status === 'failed' || status === 'expired' || status === 'cancelled';
}

type Step = 'phone' | 'otp' | 'passport' | 'pending' | 'rejected' | 'buy';

function stepOf(c: OnrampCustomer, retryPassport: boolean): Step {
  if (c.kycStatus === 'rejected' && !retryPassport) return 'rejected';
  if (!c.started || (!c.phoneSet && !c.needsOtp)) return 'phone';
  if (!c.phoneSet) return 'otp';
  if (c.kycStatus === 'pending') return 'pending';
  if (!c.identified) return 'passport';
  return 'buy';
}

/**
 * Buy USDT with rubles via SBP.
 *
 * Identification (phone, then passport) happens here and is forwarded to the
 * provider. The rate shown is the rate submitted. After payment, USDT is
 * withdrawn to the open wallet.
 */
export function BuyScreen() {
  const { t } = useTranslation();
  const pop = useNav((s) => s.pop);
  const { token } = useSession();
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const wallet = useActiveWallet();

  const customerQ = useQuery({
    queryKey: ['onrampCustomer'],
    enabled: !!token,
    queryFn: onrampCustomerApi,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });
  const customer = customerQ.data;
  const refetchCustomer = customerQ.refetch;

  const catalogue = useQuery({
    queryKey: ['onrampAssets'],
    enabled: !!token && customer?.identified === true,
    queryFn: onrampAssetsApi,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (customer?.kycStatus !== 'pending') return;
    const id = setInterval(() => {
      void refetchCustomer();
    }, 4000);
    return () => clearInterval(id);
  }, [customer?.kycStatus, refetchCustomer]);

  const [retryPassport, setRetryPassport] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [quote, setQuote] = useState<OnrampQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payId, setPayId] = useState<string | null>(null);
  const [payUrl, setPayUrl] = useState<string | null>(null);
  const [simulating, setSimulating] = useState(false);

  // Followed until it settles: the callback may never arrive, and `onramp.get`
  // is what asks the provider and starts the payout when it reports success.
  const payOrder = useQuery({
    queryKey: ['onrampOrder', payId],
    enabled: !!token && !!payId,
    queryFn: () => onrampGetApi(payId!),
    refetchInterval: (q) => (q.state.data && isFinalOrder(q.state.data.status) ? false : 3000),
  });
  const order = payOrder.data;

  useEffect(() => {
    if (order?.checkoutUrl) setPayUrl(order.checkoutUrl);
  }, [order?.checkoutUrl]);

  useEffect(() => {
    if (order?.status !== 'completed') return;
    void qc.invalidateQueries({ queryKey: ['onrampOrders'] });
    void qc.invalidateQueries({ queryKey: ['wallets'] });
  }, [order?.status, qc]);

  const fiat = catalogue.data?.fiatCurrency ?? 'RUB';
  const max = catalogue.data?.assets[0]?.maxAmount ?? null;
  const amountNum = useMemo(() => Number(amount.replace(',', '.')) || 0, [amount]);
  const aboveMax = max != null && amountNum > max;
  const shownStep: Step | 'loading' = customer ? stepOf(customer, retryPassport) : 'loading';

  useEffect(() => {
    if (shownStep !== 'buy' || !token || amountNum <= 0 || aboveMax) {
      setQuote(null);
      setQuoting(false);
      return;
    }
    let alive = true;
    setQuoting(true);
    const id = setTimeout(() => {
      onrampQuoteApi(amountNum.toFixed(2))
        .then((q) => alive && setQuote(q))
        .catch((e) => {
          if (!alive) return;
          setQuote(null);
          if (e instanceof ApiError && (e.code === 'customer_not_found' || e.code === 'kyc_required')) {
            void refetchCustomer();
          }
        })
        .finally(() => alive && setQuoting(false));
    }, 450);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [token, shownStep, amountNum, aboveMax, refetchCustomer]);

  const onAmount = (v: string) => {
    const cleaned = v
      .replace(/[^\d.,]/g, '')
      .replace(/[.,]/, '§')
      .replace(/[.,]/g, '')
      .replace('§', '.');
    setAmount(cleaned);
  };

  const fail = (e: unknown) => {
    haptic();
    showToast(buyMessage(t, e));
  };

  const sendPhone = async () => {
    if (phone.trim().length < 10 || busy) return;
    haptic('medium');
    setBusy(true);
    try {
      await onrampStartPhoneApi(phone.trim());
      await customerQ.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const sendOtp = async () => {
    if (otp.trim().length < 4 || busy) return;
    haptic('medium');
    setBusy(true);
    try {
      await onrampVerifyPhoneApi(otp.trim());
      setOtp('');
      await customerQ.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (busy) return;
    haptic();
    setBusy(true);
    try {
      await onrampResendOtpApi();
      showToast(t('buy.otpHint'));
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const sendPassport = async () => {
    if (!firstName.trim() || !lastName.trim() || !birthDate || docNumber.trim().length < 4 || busy) return;
    haptic('medium');
    setBusy(true);
    try {
      await onrampIdentifyApi({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(patronymic.trim() ? { patronymic: patronymic.trim() } : {}),
        birthDate,
        docNumber: docNumber.trim(),
      });
      setRetryPassport(false);
      await customerQ.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const canBuy = !!wallet && !!quote && !quoting && !busy && amountNum > 0 && !aboveMax;

  const buy = async () => {
    if (!canBuy || !wallet || !quote) return;
    haptic('medium');
    setBusy(true);
    try {
      const order = await onrampCreateApi({
        walletId: wallet.id,
        amount: amountNum.toFixed(2),
        quoteSignature: quote.quoteSignature,
      });
      void qc.invalidateQueries({ queryKey: ['onrampOrders'] });
      if (order.status === 'failed') {
        showToast(t('buy.failed'));
        return;
      }
      qc.setQueryData(['onrampOrder', order.id], order);
      setPayId(order.id);
      setPayUrl(order.checkoutUrl);
      // Telegram opens a link only from a tap; this still counts as one. The
      // payment screen keeps a button for when it does not.
      if (order.checkoutUrl) openLink(order.checkoutUrl);
    } catch (e) {
      fail(e);
      if (e instanceof ApiError && (e.code === 'customer_not_found' || e.code === 'kyc_required')) {
        void customerQ.refetch();
      }
    } finally {
      setBusy(false);
    }
  };

  const simulatePaid = async () => {
    if (!payId || simulating) return;
    haptic('medium');
    setSimulating(true);
    try {
      const updated = await onrampSimulatePaidApi(payId);
      qc.setQueryData(['onrampOrder', payId], updated);
      void payOrder.refetch();
    } catch (e) {
      fail(e);
    } finally {
      setSimulating(false);
    }
  };

  const startOver = () => {
    haptic();
    setPayId(null);
    setPayUrl(null);
    setQuote(null);
    setAmount('');
  };

  if (payId) {
    const status = order?.status;
    const done = status === 'completed';
    const lost = status === 'failed' || status === 'expired' || status === 'cancelled';
    const paid = !done && !lost && order?.paid === true;
    const awaiting = !done && !lost && !paid;
    const primary =
      'flex h-[56px] w-full items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white active:bg-accent-ink disabled:text-white/[0.44]';
    const secondary =
      'flex h-[48px] w-full items-center justify-center rounded-full bg-card text-[15px] font-medium text-accent active:opacity-80 disabled:opacity-50';

    const title = done
      ? t('buy.done')
      : lost
        ? t('buy.payFailed')
        : paid
          ? t('buy.paid')
          : payUrl
            ? t('buy.paying')
            : t('buy.waitingLink');
    const hint = done
      ? t('buy.doneHint')
      : lost
        ? t('buy.payFailedHint')
        : paid
          ? t('buy.paidHint')
          : t('buy.payingHint');

    return (
      <div className="page-enter flex min-h-full flex-col items-center gap-4 bg-surface px-8 pb-8 pt-16 text-center">
        {(paid || (awaiting && !payUrl)) && (
          <span className="size-8 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        )}
        <p className="text-[16px] font-medium text-ink">{title}</p>
        <p className="max-w-[280px] text-[14px] leading-[1.4] text-muted">{hint}</p>
        {order && order.cryptoAmount != null && !lost && (
          <p className="text-[15px] font-medium text-ink">
            {fmtCoin(order.cryptoAmount, order.coin).replace(/^\+/, '')}
          </p>
        )}

        <div className="mt-4 flex w-full flex-col gap-3">
          {awaiting && payUrl && (
            <button
              onClick={() => {
                haptic();
                openLink(payUrl);
              }}
              className={primary}
            >
              {t('buy.openPayment')}
            </button>
          )}
          {awaiting && catalogue.data?.stage && (
            <button onClick={simulatePaid} disabled={simulating} className={secondary}>
              {simulating ? (
                <span className="size-5 animate-spin rounded-full border-[3px] border-accent/25 border-t-accent" />
              ) : (
                t('buy.simulate')
              )}
            </button>
          )}
          {lost && (
            <button onClick={startOver} className={primary}>
              {t('buy.newOrder')}
            </button>
          )}
          {(done || paid) && (
            <button
              onClick={() => {
                haptic();
                pop();
              }}
              className={primary}
            >
              {t('buy.finish')}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (customerQ.isLoading || shownStep === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
      </div>
    );
  }

  if (customerQ.isError) {
    return (
      <div className="page-enter flex min-h-screen flex-col items-center justify-center gap-4 px-8 pb-24 text-center">
        <p className="text-[16px] font-medium text-ink">{t('buy.unavailable')}</p>
        <p className="max-w-[280px] text-[14px] leading-[1.4] text-muted">{t('buy.unavailableHint')}</p>
      </div>
    );
  }

  if (shownStep === 'phone' || shownStep === 'otp') {
    const onPhone = shownStep === 'phone';
    return (
      <div className="page-enter flex min-h-full flex-col gap-6 bg-surface px-4 pb-8 pt-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="grid size-20 place-items-center rounded-full bg-card">
            <FigIcon name="pfSms" size={36} className="text-accent" />
          </span>
          <h1 className="text-[24px] font-medium text-ink">{onPhone ? t('buy.phoneTitle') : t('buy.otpTitle')}</h1>
          <p className="max-w-[280px] text-[15px] leading-[1.44] text-muted">
            {onPhone ? t('buy.phoneHint') : t('buy.otpHint')}
          </p>
        </div>
        {onPhone ? (
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            inputMode="tel"
            autoComplete="tel"
            placeholder={t('buy.phonePlaceholder')}
            className={inputClass}
          />
        ) : (
          <input
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="0000"
            className={inputClass}
          />
        )}
        <button
          onClick={onPhone ? sendPhone : sendOtp}
          disabled={busy || (onPhone ? phone.trim().length < 10 : otp.trim().length < 4)}
          className="mt-auto flex h-[56px] w-full items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white disabled:text-white/[0.44] active:bg-accent-ink"
        >
          {busy ? (
            <span className="size-6 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
          ) : onPhone ? (
            t('buy.sendCode')
          ) : (
            t('buy.continue')
          )}
        </button>
        {!onPhone && (
          <button onClick={resend} disabled={busy} className="text-[14px] font-medium text-accent active:opacity-70">
            {t('buy.resend')}
          </button>
        )}
      </div>
    );
  }

  if (shownStep === 'pending') {
    return (
      <div className="page-enter flex min-h-screen flex-col items-center justify-center gap-4 px-8 pb-24 text-center">
        <span className="size-10 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        <p className="text-[16px] font-medium text-ink">{t('buy.kycPending')}</p>
        <p className="max-w-[280px] text-[14px] leading-[1.4] text-muted">{t('buy.kycPendingHint')}</p>
      </div>
    );
  }

  if (shownStep === 'rejected') {
    return (
      <div className="page-enter flex min-h-screen flex-col items-center justify-center gap-4 px-8 pb-24 text-center">
        <p className="text-[16px] font-medium text-ink">{t('buy.kycRejected')}</p>
        <p className="max-w-[280px] text-[14px] leading-[1.4] text-muted">{t('buy.kycRejectedHint')}</p>
        <button
          onClick={() => {
            haptic();
            setRetryPassport(true);
          }}
          className="mt-2 flex h-[56px] w-full items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white active:bg-accent-ink"
        >
          {t('buy.tryAgain')}
        </button>
      </div>
    );
  }

  if (shownStep === 'passport') {
    const ready = !!firstName.trim() && !!lastName.trim() && !!birthDate && docNumber.trim().length >= 4;
    return (
      <div className="page-enter flex min-h-full flex-col gap-4 bg-surface px-4 pb-8 pt-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-[24px] font-medium text-ink">{t('buy.passportTitle')}</h1>
          <p className="max-w-[300px] text-[15px] leading-[1.44] text-muted">{t('buy.passportHint')}</p>
        </div>
        <input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={t('buy.lastName')} className={inputClass} />
        <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={t('buy.firstName')} className={inputClass} />
        <input value={patronymic} onChange={(e) => setPatronymic(e.target.value)} placeholder={t('buy.patronymic')} className={inputClass} />
        <label className="flex flex-col gap-2">
          <span className="px-4 text-[13px] text-muted">{t('buy.birthDate')}</span>
          <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} className={inputClass} />
        </label>
        <input
          value={docNumber}
          onChange={(e) => setDocNumber(e.target.value)}
          inputMode="numeric"
          placeholder={t('buy.docNumber')}
          className={inputClass}
        />
        <button
          onClick={sendPassport}
          disabled={!ready || busy}
          className="mt-auto flex h-[56px] w-full items-center justify-center rounded-full bg-accent text-[15px] font-bold text-white disabled:text-white/[0.44] active:bg-accent-ink"
        >
          {busy ? (
            <span className="size-6 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
          ) : (
            t('buy.submitPassport')
          )}
        </button>
      </div>
    );
  }

  const error = aboveMax ? t('buy.aboveMax', { amount: `${fmtRub(max ?? 0)} ${fiat}` }) : null;

  return (
    <div className="page-enter flex min-h-full flex-col gap-6 bg-surface px-4 pb-8 pt-3">
      <p className="text-center text-[16px] font-medium text-ink">{t('buy.title')}</p>

      <div className="flex items-center gap-3 rounded-[26px] bg-card p-4">
        <CoinIcon symbol="USDT" size={36} />
        <span className="flex min-w-0 flex-col items-start">
          <span className="text-[15px] font-medium text-ink">USDT</span>
          <span className="text-[12px] text-muted">Tether (TRC-20) · {t('buy.payWithSbp')}</span>
        </span>
      </div>

      <div className="flex flex-col gap-3">
        <p className="px-4 text-[15px] font-medium text-muted">{t('buy.spend')}</p>
        <div className="flex h-[60px] items-center justify-between gap-2 rounded-[26px] bg-card py-4 pl-4 pr-3">
          <input
            value={amount}
            onChange={(e) => onAmount(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            className="[font-family:'SF_Pro_Rounded',system-ui] min-w-0 flex-1 bg-transparent text-[24px] font-medium text-ink outline-none placeholder:text-muted"
          />
          <span className="shrink-0 rounded-full bg-surface px-3 py-[6px] text-[15px] font-medium text-ink">
            {fiat}
          </span>
        </div>
        {error ? (
          <p className="px-4 text-[13px] font-medium text-negative">{error}</p>
        ) : max != null ? (
          <p className="px-4 text-[13px] text-muted">{t('buy.limitHint', { amount: `${fmtRub(max)} ${fiat}` })}</p>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 rounded-[24px] bg-card p-4">
        {quoting ? (
          <div className="flex h-[92px] items-center justify-center">
            <span className="size-6 animate-spin rounded-full border-[3px] border-accent/25 border-t-accent" />
          </div>
        ) : quote ? (
          <>
            <Row label={t('buy.rate')} value={`1 ${quote.coin} ≈ ${fmtRub(quote.exchangeRate)} ${fiat}`} />
            <div className="h-px w-full bg-hairline" />
            <Row label={t('buy.commission')} value={`${quote.commissionPercent}%`} />
            <div className="h-px w-full bg-hairline" />
            <Row
              label={t('buy.youReceive')}
              value={fmtCoin(quote.receiveAmount, quote.coin).replace(/^\+/, '')}
              strong
            />
          </>
        ) : (
          <div className="flex h-[92px] items-center justify-center px-4 text-center text-[14px] leading-[1.4] text-muted">
            {t('buy.enterAmount')}
          </div>
        )}
      </div>

      {wallet && (
        <p className="px-4 text-center text-[13px] leading-[1.4] text-muted">
          {t('buy.deliveredTo', { wallet: wallet.name, address: shortAddr(wallet.address, 6, 6) })}
        </p>
      )}

      <button
        onClick={buy}
        disabled={!canBuy}
        className={`mt-auto flex h-[56px] w-full items-center justify-center rounded-full border border-card text-[15px] font-bold transition-colors ${
          canBuy ? 'bg-accent text-white active:bg-accent-ink' : 'bg-accent text-white/[0.44]'
        }`}
      >
        {busy ? (
          <span className="size-6 animate-spin rounded-full border-[3px] border-card/30 border-t-white" />
        ) : (
          t('buy.continue')
        )}
      </button>
      <p className="px-4 text-center text-[12px] leading-[1.4] text-faint">{t('buy.handoff')}</p>
    </div>
  );
}

function Row({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <span className="text-[15px] text-faint">{label}</span>
      <span className={`text-right text-[15px] text-ink ${strong ? 'font-bold' : 'font-medium'}`}>
        {value}
      </span>
    </div>
  );
}
